import React from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMount } from '@shared/utils/hooks/useMount';
import type { PayoutPendingStepCta } from './PayoutDialog.types';

const SUPPORT_EMAIL_HREF = 'mailto:support@withjoy.com';

type PendingStepProps = Readonly<{
  onDone: () => void;
  onViewed?: () => void;
  onInteracted?: (cta: PayoutPendingStepCta) => void;
}>;

/**
 * Shown when the payout account is still onboarding or pending but Stripe returned no
 * `onboardingUrl`, so there is nothing for the couple to do until verification completes.
 */
export const PendingStep: React.FC<PendingStepProps> = ({ onDone, onViewed, onInteracted }) => {
  const { tq } = useTranslation('cashFundsAdmin');

  useMount(() => {
    onViewed?.();
  });

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(24)}
        fontWeight={600}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.3"
        marginBottom={6}
      >
        {tq`Account under review`}
      </TextV2>

      <Flex
        flexDirection="column"
        flex={1}
        justifyContent="center"
      >
        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          color="mono10"
          lineHeight="1.55"
        >
          {tq`Stripe is still verifying your payout account. You can transfer funds as soon as the review is complete. Contact us if it takes longer than a few days.`}
        </TextV2>
      </Flex>

      <ButtonV2
        onClick={onDone}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
      >
        {tq`Got it`}
      </ButtonV2>
      <ButtonV2
        as="a"
        href={SUPPORT_EMAIL_HREF}
        variant="ghost"
        intent="neutral"
        size="md"
        shape="rounded"
        fullWidth
        marginTop={2}
        onClick={() => onInteracted?.('contactSupport')}
      >
        {tq`Contact Support`}
      </ButtonV2>
    </Flex>
  );
};
